/**
 * 🎯 모듈 그리기 관리 클래스
 * 그리기 시작, 진행, 취소, 완료를 담당
 */
import Draw, { createBox } from 'ol/interaction/Draw.js';
import Polygon from 'ol/geom/Polygon.js';
import i18n from '@/plugins/i18n';

export class DrawingManager {
  constructor(map, vectorSource) {
    this.map = map;
    this.vectorSource = vectorSource;

    // 그리기 상태
    this.isDrawing = false;
    this.drawingPoints = [];
    this.drawnPolygon = null;
    this.drawInteraction = null;

    // 🆕 그리기 모드 설정
    this.drawingMode = 'rectangle';        // 기본값: 직사각형
    this.drawType = 'Circle';              // 🆕 기본값: Circle 타입
    this.geometryFunction = createBox();   // 🆕 기본값: createBox() 함수
    this.maxPoints = 2;                    // 🆕 기본값: 2점 (직사각형)
    this.keepDrawing = false;              // 🆕 기본값: 한 번만 그리기

    // 취소 관련 요소들
    this.drawingCancelOverlay = null;
    this.drawingKeyListener = null;
    this.drawingRightClickListener = null;
    this.drawingContainerRightClickListener = null;
    this.drawingCustomEventListener = null;

    // 콜백 함수들
    this.onDrawingStart = null;
    this.onDrawingEnd = null;
    this.onDrawingCancel = null;
  }

  /**
   * 🆕 그리기 모드 설정
   * @param {string} mode - 그리기 모드 ('rectangle', 'polygon')
   */
  setDrawingMode(mode) {
    this.drawingMode = mode;

    switch(mode) {
      case 'rectangle':
        this.drawType = 'Circle';           // 🆕 'Circle' 타입 사용 (OpenLayers 예제 참고)
        // 🆕 지도 회전을 고려한 커스텀 직사각형 그리기 함수
        this.geometryFunction = this.createRotationCorrectedRectangle();
        this.maxPoints = 2;                 // 🆕 직사각형: 2점 필요
        console.log('🎯 직사각형 모드 설정 (타입: Circle + 회전 보정 직사각형, 최대 2점)');
        break;
      case 'polygon':
        this.drawType = 'Polygon';          // 'Polygon' 타입 사용
        this.geometryFunction = undefined;  // 기본 다각형 그리기
        this.maxPoints = 4;                 // 🆕 다각형: 4
        // 점
        console.log('🎯 다각형 모드 설정 (타입: Polygon, 최대 4점)');
        break;
      default:
        console.warn(`⚠️ 알 수 없는 그리기 모드: ${mode}, 기본값(직사각형) 사용`);
        this.drawingMode = 'rectangle';
        this.drawType = 'Circle';
        this.geometryFunction = this.createRotationCorrectedRectangle();
        this.maxPoints = 2;
    }

    console.log(`🔄 그리기 모드 변경: ${this.drawingMode} (타입: ${this.drawType}, 최대 포인트: ${this.maxPoints})`);
  }

  /**
   * 그리기 시작
   * @param {Function} onStart - 그리기 시작 시 콜백
   * @param {Function} onEnd - 그리기 완료 시 콜백
   * @param {Function} onCancel - 그리기 취소 시 콜백
   */
  startDrawing(onStart = null, onEnd = null, onCancel = null) {
    if (this.isDrawing) {
      console.warn('⚠️ 이미 그리기 중입니다.');
      return;
    }

    // 콜백 함수 설정
    this.onDrawingStart = onStart;
    this.onDrawingEnd = onEnd;
    this.onDrawingCancel = onCancel;

    // 이전 그리기 인터랙션 제거
    this.removeDrawInteraction();

    // 그리기 상태 초기화
    this.isDrawing = true;
    this.drawingPoints = [];
    this.showDrawingCancelButton();

    // 그리기 인터랙션 생성
    this.createDrawInteraction();

    // 취소 이벤트 리스너 설정
    this.setupDrawingCancelListeners();

    // 맵에 그리기 인터랙션 추가
    this.map.addInteraction(this.drawInteraction);

    console.log('🎯 모듈 그리기 시작');

    // 시작 콜백 실행
    if (this.onDrawingStart) {
      this.onDrawingStart();
    }
  }

  /**
   * 그리기 인터랙션 생성
   */
  createDrawInteraction() {
    const drawOptions = {
      source: this.vectorSource,
      type: this.drawType,                 // 🆕 'Circle' 또는 'Polygon'
      geometryFunction: this.geometryFunction // 🆕 createBox() 또는 undefined
    };

    // 🆕 maxPoints가 undefined가 아닐 때만 추가
    if (this.maxPoints !== undefined) {
      drawOptions.maxPoints = this.maxPoints;
    }

    this.drawInteraction = new Draw(drawOptions);

    // 그리기 시작 이벤트
    this.drawInteraction.on('drawstart', (_event) => {
      console.log('🎯 그리기 시작됨');
      this.drawingPoints = [];
    });

    // 그리기 완료 이벤트
    this.drawInteraction.on('drawend', (event) => {
      this.drawnPolygon = event.feature;
      this.completeDrawing();
    });
  }

  /**
   * 그리기 완료 처리
   */
  completeDrawing() {
    console.log('✅ 그리기 완료');

    // 🆕 그리기 완료 콜백 실행 (현재 그리기 모드 정보 포함)
    if (this.onDrawingEnd) {
      this.onDrawingEnd(this.drawnPolygon, this.drawingMode);
    }

    // 🆕 간단한 플래그로 계속 그릴지 판단
    if (this.keepDrawing) {
      console.log('🔄 계속 그리기 모드: 같은 모드로 계속 그리기');
      // 그리기 상태 유지, 새로운 그리기 준비
      this.drawnPolygon = null;
      this.drawingPoints = [];
    } else {
      console.log('🔄 단발성 그리기: 그리기 모드 종료');
      // 그리기 모드 완전 종료
      this.exitDrawingMode();
    }
  }

  /**
   * 🆕 그리기 모드 종료 (연속 그리기 모드에서 완전히 나가기)
   */
  exitDrawingMode() {
    if (!this.isDrawing) return;

    console.log('🔄 그리기 모드 종료');

    // 그리기 인터랙션 제거
    this.removeDrawInteraction();

    // 그리기 상태 완전 초기화
    this.isDrawing = false;
    this.drawingPoints = [];
    this.drawnPolygon = null;
    this.hideDrawingCancelButton();

    // 이벤트 리스너 정리
    this.cleanupDrawingCancelListeners();

    // 그리기 모드 초기화
    this.drawingMode = null;
    this.drawType = null;
    this.maxPoints = null;

    console.log('✅ 그리기 모드 종료 완료');
  }

  /**
   * 그리기 취소
   */
  cancelDrawing() {
    if (!this.isDrawing) return;

    console.log('🔄 모듈 그리기 취소 실행');

    // 그리기 인터랙션 제거
    this.removeDrawInteraction();

    // 임시로 그려진 피처들 제거
    if (this.drawnPolygon) {
      this.vectorSource.removeFeature(this.drawnPolygon);
      this.drawnPolygon = null;
    }

    // 그리기 상태 초기화
    this.isDrawing = false;
    this.drawingPoints = [];
    this.hideDrawingCancelButton();

    // 이벤트 리스너 정리
    this.cleanupDrawingCancelListeners();

    // 취소 콜백 실행
    if (this.onDrawingCancel) {
      this.onDrawingCancel();
    }

    console.log('✅ 모듈 그리기 취소 완료');
  }

  /**
   * 그리기 인터랙션 제거
   */
  removeDrawInteraction() {
    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
  }

  /**
   * 🆕 그리기 취소 조건 확인 함수
   */
  shouldCancelDrawing(event) {
    // ESC 키
    if (event.key === 'Escape') {
      return true;
    }

    // 우클릭 (마우스 우버튼)
    if (event.button === 2 || (event.originalEvent && event.originalEvent.button === 2)) {
      return true;
    }

    return false;
  }

  /**
   * 🆕 연속 그리기 모드 설정
   * @param {boolean} value - true: 연속 그리기, false: 단발성 그리기
   */
  setKeepDrawing(value) {
    this.keepDrawing = value;
    console.log(`🔄 연속 그리기 모드 설정: ${value ? '연속' : '단발성'}`);
  }

  /**
   * 🆕 지도 회전을 고려한 직사각형 그리기 함수
   * 사용자가 보는 화면의 가로/세로 방향과 평행하게 직사각형 생성
   * OpenLayers Star 예제를 참조하여 구현
   */
  createRotationCorrectedRectangle() {
    return (coordinates, geometry) => {
      if (!this.map) {
        // 맵이 없으면 기본 createBox 사용
        return createBox()(coordinates, geometry);
      }

      try {
        const view = this.map.getView();
        const rotation = view.getRotation();

        // 지도가 회전되지 않았으면 기본 createBox 사용
        if (Math.abs(rotation) < 0.001) {
          return createBox()(coordinates, geometry);
        }

        console.log(`🔄 회전 보정 직사각형 그리기: 지도 회전각 ${(rotation * 180 / Math.PI).toFixed(2)}°`);

        // 🎯 핵심: 지도 회전의 반대 방향으로 좌표 회전
        const rotatedCoordinates = coordinates.map(coord => {
          // 지도 회전의 반대 방향으로 회전
          const cos = Math.cos(-rotation);
          const sin = Math.sin(-rotation);

          return [
            coord[0] * cos - coord[1] * sin,
            coord[0] * sin + coord[1] * cos
          ];
        });

        // 🎯 회전된 좌표로 직사각형 생성 (수평/수직 방향)
        const center = rotatedCoordinates[0];
        const last = rotatedCoordinates[rotatedCoordinates.length - 1];

        // 직사각형의 네 모서리 좌표 계산
        const dx = last[0] - center[0];
        const dy = last[1] - center[1];

        const rectangleCoordinates = [
          [center[0], center[1]],                    // 좌하단
          [center[0] + dx, center[1]],              // 우하단
          [center[0] + dx, center[1] + dy],         // 우상단
          [center[0], center[1] + dy],              // 좌상단
          [center[0], center[1]]                    // 닫기 (첫 점과 동일)
        ];

        // 🎯 지도 회전 방향으로 다시 회전하여 원래 방향으로 복원
        const finalCoordinates = rectangleCoordinates.map(coord => {
          const cos = Math.cos(rotation);
          const sin = Math.sin(rotation);

          return [
            coord[0] * cos - coord[1] * sin,
            coord[0] * sin + coord[1] * cos
          ];
        });

        // 🎯 최종 직사각형 생성
        if (!geometry) {
          geometry = new Polygon([finalCoordinates]);
        } else {
          geometry.setCoordinates([finalCoordinates]);
        }

        console.log('✅ 회전 보정 직사각형 생성 완료');
        return geometry;

      } catch (error) {
        console.error('❌ 회전 보정 직사각형 생성 중 오류:', error);
        // 오류 발생 시 기본 createBox 사용
        return createBox()(coordinates, geometry);
      }
    };
  }

  /**
   * 그리기 취소 이벤트 리스너 설정
   */
  setupDrawingCancelListeners() {
    // 🆕 통합된 그리기 취소 이벤트 리스너
    this.drawingKeyListener = (event) => {
      if (this.isDrawing && this.shouldCancelDrawing(event)) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🔄 ESC 키로 모듈 그리기 취소');
        this.cancelDrawing();
      }
    };

    // 🆕 맵 우클릭 이벤트 리스너 (통합)
    this.drawingRightClickListener = (event) => {
      if (this.isDrawing && this.shouldCancelDrawing(event)) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🔄 우클릭으로 모듈 그리기 취소');
        this.cancelDrawing();
        return false; // 이벤트 전파 완전 차단
      }
    };

    // 🆕 맵 컨테이너 우클릭 이벤트 리스너 (통합)
    const mapContainer = this.map.getViewport();
    this.drawingContainerRightClickListener = (event) => {
      if (this.isDrawing && this.shouldCancelDrawing(event)) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🔄 맵 컨테이너 우클릭으로 모듈 그리기 취소');
        this.cancelDrawing();
        return false; // 이벤트 전파 완전 차단
      }
    };

    // 🆕 맵 전체 우클릭 이벤트 리스너 (컨텍스트 메뉴 완전 차단, 통합)
    this.drawingMapRightClickListener = (event) => {
      if (this.isDrawing && this.shouldCancelDrawing(event)) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🔄 맵 전체 우클릭으로 모듈 그리기 취소');
        this.cancelDrawing();
        return false;
      }
    };

    // 커스텀 취소 이벤트 리스너
    this.drawingCustomEventListener = (event) => {
      if (event.type === 'cancel-drawing') {
        console.log('🔄 커스텀 이벤트로 모듈 그리기 취소');
        this.cancelDrawing();
      }
    };

    // 🆕 성능 최적화: 이벤트 리스너 등록
    document.addEventListener('keydown', this.drawingKeyListener);

    // 🆕 맵 클릭 이벤트는 하나로 통합 (중복 제거)
    this.map.on('click', this.drawingRightClickListener);

    // 🆕 컨텍스트 메뉴 이벤트만 별도 등록
    mapContainer.addEventListener('contextmenu', this.drawingContainerRightClickListener);

    // 🆕 커스텀 이벤트 리스너 등록
    if (this.drawingCancelOverlay) {
      this.drawingCancelOverlay.addEventListener('cancel-drawing', this.drawingCustomEventListener);
    }
  }

  /**
   * 그리기 취소 이벤트 리스너 정리
   */
  cleanupDrawingCancelListeners() {
    if (this.drawingKeyListener) {
      document.removeEventListener('keydown', this.drawingKeyListener);
      this.drawingKeyListener = null;
    }

    if (this.drawingRightClickListener) {
      this.map.un('click', this.drawingRightClickListener);
      this.drawingRightClickListener = null;
    }

    if (this.drawingContainerRightClickListener) {
      const mapContainer = this.map?.getViewport();
      if (mapContainer) {
        mapContainer.removeEventListener('contextmenu', this.drawingContainerRightClickListener);
      }
      this.drawingContainerRightClickListener = null;
    }

    if (this.drawingCustomEventListener && this.drawingCancelOverlay) {
      this.drawingCancelOverlay.removeEventListener('cancel-drawing', this.drawingCustomEventListener);
      this.drawingCustomEventListener = null;
    }
  }

  /**
   * 그리기 취소 버튼 표시
   */
  applyDrawingCancelOverlayCopy() {
    if (!this.drawingCancelOverlay) return;
    const statusEl = this.drawingCancelOverlay.querySelector('[data-i18n="drawInProgress"]');
    const cancelBtnEl = this.drawingCancelOverlay.querySelector('[data-i18n="drawCancel"]');
    const hintEl = this.drawingCancelOverlay.querySelector('[data-i18n="drawCancelHint"]');
    if (statusEl) statusEl.textContent = i18n.t('panelPlanning.drawInProgress');
    if (cancelBtnEl) cancelBtnEl.textContent = i18n.t('panelPlanning.drawCancel');
    if (hintEl) hintEl.textContent = i18n.t('panelPlanning.drawCancelHint');
  }

  showDrawingCancelButton() {
    if (!this.drawingCancelOverlay) {
      this.drawingCancelOverlay = document.createElement('div');
      this.drawingCancelOverlay.className = 'drawing-cancel-overlay';
      this.drawingCancelOverlay.innerHTML = `
        <div class="drawing-cancel-panel">
          <div class="drawing-status">
            <span class="status-icon">✏️</span>
            <span data-i18n="drawInProgress"></span>
          </div>
          <button 
            class="drawing-cancel-btn"
            onclick="this.closest('.drawing-cancel-overlay').dispatchEvent(new CustomEvent('cancel-drawing'))"
          >
            <span class="btn-icon">✕</span>
            <span data-i18n="drawCancel"></span>
          </button>
          <div class="drawing-hint">
            <small data-i18n="drawCancelHint"></small>
          </div>
        </div>
      `;

      const mapContainer = this.map.getViewport();
      mapContainer.appendChild(this.drawingCancelOverlay);
    }

    this.applyDrawingCancelOverlayCopy();
    this.drawingCancelOverlay.style.display = 'block';
  }

  /**
   * 그리기 취소 버튼 숨기기
   */
  hideDrawingCancelButton() {
    if (this.drawingCancelOverlay) {
      this.drawingCancelOverlay.style.display = 'none';
    }
  }

  /**
   * 그리기 상태 확인
   */
  getDrawingState() {
    return {
      isDrawing: this.isDrawing,
      drawingPoints: this.drawingPoints,
      drawnPolygon: this.drawnPolygon
    };
  }

  /**
   * 🆕 그리기 중인지 확인 (외부에서 접근 가능)
   */
  isCurrentlyDrawing() {
    return this.isDrawing;
  }

  /**
   * 리소스 정리
   */
  destroy() {
    this.cancelDrawing();
    this.cleanupDrawingCancelListeners();

    if (this.drawingCancelOverlay) {
      this.drawingCancelOverlay.remove();
      this.drawingCancelOverlay = null;
    }

    this.map = null;
    this.vectorSource = null;
  }
}
